import React, { useState, useMemo } from 'react'
import {
  Box,
  Typography,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  ListSubheader,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Divider,
  Card,
  CardContent,
  Button,
  Chip,
  ListItemButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tabs,
  Tab,
  Tooltip,
  Menu,
  ListItemIcon,
  ToggleButtonGroup,
  ToggleButton,
  InputAdornment,
} from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft'
import FormatAlignCenterIcon from '@mui/icons-material/FormatAlignCenter'
import FormatAlignRightIcon from '@mui/icons-material/FormatAlignRight'
import RestartAltIcon from '@mui/icons-material/RestartAlt'
import VisibilityIcon from '@mui/icons-material/Visibility'
import EditIcon from '@mui/icons-material/Edit'
import RefreshIcon from '@mui/icons-material/Refresh'
import TableChartIcon from '@mui/icons-material/TableChart'
import AnalyticsIcon from '@mui/icons-material/Analytics'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import CloseIcon from '@mui/icons-material/Close'
import UndoIcon from '@mui/icons-material/Undo'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty'
import ReplayIcon from '@mui/icons-material/Replay'
import SettingsIcon from '@mui/icons-material/Settings'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import ContentPasteIcon from '@mui/icons-material/ContentPaste'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ReactMarkdown from 'react-markdown'
import axios from 'axios'
import { useQuery } from '@tanstack/react-query'

import Tree from '../../tree/Tree'
import { GenNode } from '../../../types/carbon/models/GenNode'
import { MultiTableConfigTable, MultiTableAnalysisWidgetData, MultiTableAnalysisItem, TableAnalysisContext, Prompt, ChartTitleDesign } from '../../../types/gotypes'
import type { CardTextWrite, CardWriteHandler } from '../../../types/cardWrite'
import { getNodeTitle } from '../../../utils/nodeTypes'
import { getAugementedTOC } from '../../../utils/multitables'
import { extractErrorMessage, extractErrorCode } from '../../../utils/apitools'
import { REGENERATE_NOT_PERMITTED_CODE } from '../../../utils/dashboardOwnership'
import { isAiRefusalCode } from '../../../utils/aiAccess'
import { cardJobFilters, cardSendJobFilters, jobFiltersEqual } from '../../../utils/dashboardFilters'
import useMultiTableAnalysis from '../../../hooks/useMultiTableAnalysis'
import useDashboardMultiTable from '../../../hooks/useDashboardMultiTable'
import { usePrompts } from '../../../hooks/usePrompts'
import { PlatinumTableWrapper } from '../../platinum-table-new/PlatinumTableWrapper'
import { API_BASE_URL } from '../../../constants/system'
import { TableCitationModal } from './TableCitationModal'
import { CitationReferenceList, renderCitationText } from '../../citations/CitationMarker'
import { createCitationNumbering, type CitationCell, type CitationNumbering, type CitationReference } from '../../../utils/citations'
import useCardTextEditor from './useCardTextEditor'
import { InstructionsField } from './genai/InstructionsField'
import { FilesComingSoonSlot } from './genai/FilesComingSoonSlot'
import { MissingReferencesBanner, ReferenceDecision } from './genai/MissingReferencesBanner'
import { useMissingTableReferences } from './genai/useMissingTableReferences'
import { FilterSignpostLine, useFilterSignposts } from '../FilterSignpost'
import { tableFullPath, GuidedToggleState, sourceTablePathsChanged, templatedSettingsDiffer } from './genai/wizardHelpers'
import { chipDisplayLabel, extractTableChips, formatAttributionDate, removeTableCitation } from './multiTableCitations'


/** #1219 gate 2: a regenerate start refused because the caller may not
 *  overwrite this dashboard, keyed on the server's `code` (not the HTTP
 *  status — a 403 can mean other things). */
const isRegeneratePermissionError = (err: unknown): boolean => {
  const code = extractErrorCode(err)
  // Also the AI access refusals (ai_access_level / ai_budget) the same route
  // can return: a permission decision whose sentence belongs on the card
  // (audit A03-12).
  return code === REGENERATE_NOT_PERMITTED_CODE || isAiRefusalCode(code)
}

/** Group prompts by category and return sorted groups with items */
function groupPromptsByCategory(prompts: Prompt[]): { category: string; items: Prompt[] }[] {
  const groups: Record<string, Prompt[]> = {}
  for (const p of prompts) {
    const cat = p.category || 'Uncategorised'
    if (!groups[cat]) groups[cat] = []
    groups[cat].push(p)
  }
  return Object.keys(groups).sort().map(cat => ({ category: cat, items: groups[cat] }))
}

/** Build a display label for a prompt showing section context */
function getPromptLabel(p: Prompt): string {
  const parts: string[] = []
  if (p.pillar) parts.push(p.pillar)
  if (p.section) parts.push(p.section)
  if (parts.length > 0) return `${parts.join(' / ')} — ${p.name}`
  return p.name
}

/** Render grouped prompt MenuItems for a Select dropdown */
function renderGroupedPromptItems(prompts: Prompt[]): React.ReactNode[] {
  const groups = groupPromptsByCategory(prompts)
  const items: React.ReactNode[] = []
  for (const group of groups) {
    items.push(
      <ListSubheader key={`header-${group.category}`} sx={{ bgcolor: 'grey.100', fontWeight: 600, lineHeight: '32px' }}>
        {group.category}
      </ListSubheader>
    )
    for (const p of group.items) {
      items.push(
        <MenuItem key={p.id} value={p.id} sx={{ pl: 3 }}>
          <Box>
            <Typography variant="body2">{getPromptLabel(p)}</Typography>
            {p.description && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                {p.description}
              </Typography>
            )}
          </Box>
        </MenuItem>
      )
    }
  }
  return items
}

/** Shared 2-part content for a single stage: Instructions (with Context Library) → Additional Context */
const StageContent: React.FC<{
  stage: 'analysis' | 'synthesis'
  tableCount: number
  instructions: string
  userContext: string
  onInstructionsChange: (v: string) => void
  onUserContextChange: (v: string) => void
  prompts: Prompt[]
  onPromptSelect: (id: string) => void
  mentionSources: string[]
  guidedToggles: GuidedToggleState
  onGuidedTogglesChange: (v: GuidedToggleState) => void
}> = ({
  stage, tableCount, instructions, userContext,
  onInstructionsChange, onUserContextChange,
  prompts, onPromptSelect, mentionSources,
  guidedToggles, onGuidedTogglesChange,
}) => {
  const isAnalysis = stage === 'analysis'

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Alert severity="info" sx={{ py: 0.25, '& .MuiAlert-message': { fontSize: '0.75rem' } }}>
        {isAnalysis
          ? 'These settings apply to each individual table analysis. The AI analyses one table at a time.'
          : `These settings apply once after all ${tableCount} tables have been analysed. The AI combines the individual analyses into a single summary.`}
      </Alert>

      {/* Instructions with Context Library dropdown */}
      <Box>
        {prompts.length > 0 && (
          <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
            <InputLabel>Context Library</InputLabel>
            <Select
              value=""
              label="Context Library"
              onChange={(e) => onPromptSelect(e.target.value)}
              renderValue={() => 'Load from library...'}
              MenuProps={{ PaperProps: { sx: { maxHeight: 400 } } }}
            >
              <MenuItem value="" disabled>
                <em>Select a prompt to load into instructions</em>
              </MenuItem>
              {renderGroupedPromptItems(prompts)}
            </Select>
          </FormControl>
        )}
        <InstructionsField
          label={isAnalysis ? 'Analysis Instructions' : 'Synthesis Instructions'}
          value={instructions}
          onChange={onInstructionsChange}
          mentionSources={mentionSources}
          rows={4}
          helperText={
            (isAnalysis
              ? 'Variables: {{tableName}}, {{table1}}, {{table2}}, {{tableCount}}, {{sectionName}}. '
              : 'Variables: {{table1}}, {{table2}}, {{tableCount}}, {{sectionName}}. ') +
            "Use @ to tell the AI which data you're talking about."
          }
          guidedToggles={guidedToggles}
          onGuidedTogglesChange={onGuidedTogglesChange}
        />
      </Box>

      <Divider />

      {/* Additional Context */}
      <TextField
        fullWidth
        multiline
        rows={2}
        size="small"
        label="Additional Context"
        placeholder={isAnalysis
          ? 'Extra context appended to each table analysis prompt.'
          : 'Extra context appended to the synthesis prompt.'}
        value={userContext}
        onChange={(e) => onUserContextChange(e.target.value)}
        InputProps={{ sx: { fontFamily: 'monospace', fontSize: '0.8rem' } }}
      />
    </Box>
  )
}

/** Step-based prompt configuration: single panel for 1 table, tabs for 2+ */
const StepTabs: React.FC<{
  tables: MultiTableConfigTable[]
  analysisInstructions: string
  synthesisInstructions: string
  userContext: string
  synthesisUserContext: string
  tableAnalysisPrompts: Prompt[]
  synthesisPrompts: Prompt[]
  onAnalysisInstructionsChange: (v: string) => void
  onSynthesisInstructionsChange: (v: string) => void
  onUserContextChange: (v: string) => void
  onSynthesisUserContextChange: (v: string) => void
  onTableAnalysisPromptSelect: (id: string) => void
  onSynthesisPromptSelect: (id: string) => void
  analysisGuidedToggles: GuidedToggleState
  synthesisGuidedToggles: GuidedToggleState
  onAnalysisGuidedTogglesChange: (v: GuidedToggleState) => void
  onSynthesisGuidedTogglesChange: (v: GuidedToggleState) => void
}> = ({
  tables, analysisInstructions, synthesisInstructions, userContext, synthesisUserContext,
  tableAnalysisPrompts, synthesisPrompts,
  onAnalysisInstructionsChange, onSynthesisInstructionsChange, onUserContextChange, onSynthesisUserContextChange,
  onTableAnalysisPromptSelect, onSynthesisPromptSelect,
  analysisGuidedToggles, synthesisGuidedToggles,
  onAnalysisGuidedTogglesChange, onSynthesisGuidedTogglesChange,
}) => {
  const hasSynthesis = tables.length > 1
  const [activeStep, setActiveStep] = useState(0)
  const step = hasSynthesis ? activeStep : 0
  const mentionSources = tables.map(t => t.displayName)

  // Single table — no tabs, just a section header
  if (!hasSynthesis) {
    return (
      <Paper variant="outlined" sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        <Typography variant="subtitle2" sx={{ p: 1.5, bgcolor: 'grey.50', borderBottom: 1, borderColor: 'divider' }}>
          Table Analysis
        </Typography>
        <Box sx={{ p: 2 }}>
          <StageContent
            stage="analysis"
            tableCount={tables.length}
            instructions={analysisInstructions}
            userContext={userContext}
            onInstructionsChange={onAnalysisInstructionsChange}
            onUserContextChange={onUserContextChange}
            prompts={tableAnalysisPrompts}
            onPromptSelect={onTableAnalysisPromptSelect}
            mentionSources={mentionSources}
            guidedToggles={analysisGuidedToggles}
            onGuidedTogglesChange={onAnalysisGuidedTogglesChange}
          />
        </Box>
      </Paper>
    )
  }

  // Multiple tables — tabbed
  return (
    <Paper variant="outlined" sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
      <Tabs
        value={step}
        onChange={(_e, v) => setActiveStep(v)}
        sx={{
          minHeight: 36,
          bgcolor: 'grey.50',
          borderBottom: 1,
          borderColor: 'divider',
          '& .MuiTab-root': { minHeight: 36, py: 0.5, textTransform: 'none', fontWeight: 600, fontSize: '0.8rem' },
        }}
      >
        <Tab label="1. Table Analysis" />
        <Tab label="2. Synthesis" />
      </Tabs>

      <Box sx={{ p: 2 }}>
        {step === 0 && (
          <StageContent
            stage="analysis"
            tableCount={tables.length}
            instructions={analysisInstructions}
            userContext={userContext}
            onInstructionsChange={onAnalysisInstructionsChange}
            onUserContextChange={onUserContextChange}
            prompts={tableAnalysisPrompts}
            onPromptSelect={onTableAnalysisPromptSelect}
            mentionSources={mentionSources}
            guidedToggles={analysisGuidedToggles}
            onGuidedTogglesChange={onAnalysisGuidedTogglesChange}
          />
        )}
        {step === 1 && (
          <StageContent
            stage="synthesis"
            tableCount={tables.length}
            instructions={synthesisInstructions}
            userContext={synthesisUserContext}
            onInstructionsChange={onSynthesisInstructionsChange}
            onUserContextChange={onSynthesisUserContextChange}
            prompts={synthesisPrompts}
            onPromptSelect={onSynthesisPromptSelect}
            mentionSources={mentionSources}
            guidedToggles={synthesisGuidedToggles}
            onGuidedTogglesChange={onSynthesisGuidedTogglesChange}
          />
        )}
      </Box>
    </Paper>
  )
}

interface MultiTableAnalysisWidgetEditorProps {
  customer: string
  job: string
  data: MultiTableAnalysisWidgetData | undefined
  tables: MultiTableConfigTable[]
  title: string
  /** #1247 item 1: title formatting, the chart widgets' ChartTitleDesign. */
  titleDesign?: ChartTitleDesign
  userContext: string
  synthesisUserContext: string
  analysisInstructions: string
  synthesisInstructions: string
  analysisGuidedToggles: GuidedToggleState
  synthesisGuidedToggles: GuidedToggleState
  onTablesChange: (tables: MultiTableConfigTable[]) => void
  onTitleChange: (title: string) => void
  onTitleDesignChange?: (design: ChartTitleDesign | undefined) => void
  onUserContextChange: (userContext: string) => void
  onSynthesisUserContextChange: (synthesisUserContext: string) => void
  onAnalysisInstructionsChange: (instructions: string) => void
  onSynthesisInstructionsChange: (instructions: string) => void
  onAnalysisGuidedTogglesChange: (toggles: GuidedToggleState) => void
  onSynthesisGuidedTogglesChange: (toggles: GuidedToggleState) => void
}

/**
 * Editor component for configuring a multi-table analysis widget.
 * Shows job selector and TOC tree on left, selected tables on right.
 * Supports cross-job multi-tables where each table can be from a different job.
 */
export const MultiTableAnalysisWidgetEditor: React.FC<MultiTableAnalysisWidgetEditorProps> = ({
  customer,
  job,
  tables,
  title,
  userContext,
  synthesisUserContext,
  analysisInstructions,
  synthesisInstructions,
  analysisGuidedToggles,
  synthesisGuidedToggles,
  onTablesChange,
  onTitleChange,
  titleDesign,
  onTitleDesignChange,
  onUserContextChange,
  onSynthesisUserContextChange,
  onAnalysisInstructionsChange,
  onSynthesisInstructionsChange,
  onAnalysisGuidedTogglesChange,
  onSynthesisGuidedTogglesChange,
}) => {
  const [expanded, setExpanded] = useState<string[]>([])
  const [selected, setSelected] = useState<string>('')

  // Fetch available prompts for this customer
  const { prompts: tableAnalysisPrompts } = usePrompts({ customer, promptType: 'table_analysis' })
  const { prompts: synthesisPrompts } = usePrompts({ customer, promptType: 'synthesis' })

  // Always fetch this job's TOC directly rather than trusting account.toc:
  // that's only ever populated by visiting the Tables page for a job (see
  // contexts/account.tsx's reloadTOC — nothing else calls it), so relying on
  // it here left this tree empty with zero network requests whenever a
  // dashboard was edited without a prior Tables visit for the same job in
  // this session.
  const tocQuery = useQuery({
    queryKey: ['toc', customer, job],
    enabled: Boolean(customer && job),
    queryFn: async () => {
      const res = await axios.get<{ toc: GenNode[] }>(
        `${API_BASE_URL}/job/${customer}/${job}/toc`
      )
      return res.data.toc || []
    },
  })

  const rawToc = tocQuery.data || []
  const tocLoading = tocQuery.isLoading

  // Use augmented TOC to ensure proper deep cloning of nodes with children
  const augmentedToc = useMemo(() => {
    return getAugementedTOC({
      toc: rawToc,
      multiTables: [], // Empty since we don't need multi-table folder for selection
    })
  }, [rawToc])

  const toc = augmentedToc.nodes

  const isInMultiTable = (tablePath: string) => {
    return tables.some(table => {
      const fullPath = table.folderPath ? `${table.folderPath}/${table.filename}` : table.filename
      return fullPath === tablePath
    })
  }

  const handleNodeSelectToggle = (itemId: string, node: GenNode, isSelected: boolean) => {
    if (!isSelected) return

    // Only handle Table nodes
    if (node.type !== 'Table') {
      setSelected(itemId)
      return
    }

    // Get table info
    const filename = node.value1 || ''
    const folderPath = node.value2 || ''
    const fullPath = folderPath ? `${folderPath}/${filename}` : filename

    // If already in multi-table, don't add again
    if (isInMultiTable(fullPath)) {
      return
    }

    // Add table to the list with selected job for cross-job support
    const newTable: MultiTableConfigTable = {
      displayName: getNodeTitle(node),
      folderPath: folderPath,
      filename: filename,
      job,
    }
    onTablesChange([...tables, newTable])
    setSelected(itemId)
  }

  const handleNodeExpandToggle = (itemId: string, node: GenNode, isExpanded: boolean) => {
    if (isExpanded) {
      setExpanded([...expanded, itemId])
    } else {
      setExpanded(expanded.filter(id => id !== itemId))
    }
  }

  const handleRemoveTable = (index: number) => {
    onTablesChange(tables.filter((_, i) => i !== index))
  }

  const handleTableAnalysisPromptSelect = (promptId: string) => {
    if (!promptId) return

    // Load prompt content into analysis instructions
    const prompt = tableAnalysisPrompts.find((p: Prompt) => p.id === promptId)
    if (prompt) {
      onAnalysisInstructionsChange(prompt.content)
    }

    // Auto-populate tables from the prompt's tables field
    if (prompt?.tables?.length) {
      const newTables: MultiTableConfigTable[] = prompt.tables.map((tablePath: string) => {
        const parts = tablePath.split('/')
        const filename = parts[parts.length - 1]
        const folderPath = parts.length > 1 ? parts.slice(0, -1).join('/') : ''
        const displayName = filename.replace(/\.cbt$/i, '')
        return {
          displayName,
          folderPath,
          filename,
          job,
        }
      })
      onTablesChange(newTables)
    }
  }

  const handleSynthesisPromptSelect = (promptId: string) => {
    if (!promptId) return
    // Load prompt content into synthesis instructions
    const prompt = synthesisPrompts.find((p: Prompt) => p.id === promptId)
    if (prompt) {
      onSynthesisInstructionsChange(prompt.content)
    }
  }

  // Fetch system prompts from API (cached across renders)
  const systemPromptsQuery = useQuery({
    queryKey: ['system-prompts', customer, job],
    enabled: Boolean(customer && job),
    staleTime: 5 * 60 * 1000, // 5 minutes
    queryFn: async () => {
      // Use a dummy multi-table ID — the endpoint only needs customer/job for merged config
      const res = await axios.get(`${API_BASE_URL}/multitables/${customer}/${job}/_/prompt-stack`)
      return res.data as {
        base_system_prompt: string
        percentage_priority_guidance: string
        merged_table_analysis_rules: string
        merged_synthesis_rules: string
        assembled_system_prompt: string
      }
    },
  })

  const systemPrompts = systemPromptsQuery.data

  return (
    <Box sx={{ display: 'flex', gap: 2, height: '100%', minHeight: 400 }}>
      {/* Left column (#1132): search + folder tree ONLY — no "Select Tables"
          box, no job label (we know what job we're in; #961's no-job-picker
          rule is unchanged, the per-table `job` field stays for multi-job
          later). The tree scrolls INSIDE this column, so the scrollbar sits
          beside the tree rather than at the far edge of the whole modal. */}
      <Box sx={{ flex: '0 0 32%', minWidth: 300, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <Box sx={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
          {tocLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <Tree
              searchEnabled={true}
              searchPlaceholder="Search tables..."
              nodes={toc}
              expanded={expanded}
              selected={selected}
              onNodeSelectToggle={handleNodeSelectToggle}
              onNodeExpandToggle={handleNodeExpandToggle}
            />
          )}
        </Box>
        <Box sx={{ pt: 1 }}>
          <FilesComingSoonSlot />
        </Box>
      </Box>

      {/* Right column (#1132): ~two-thirds of the dialog — Title, selected
          tables, System Context, then the analysis steps. The width is what
          keeps Analysis Instructions from forcing a scroll past Save. */}
      <Box sx={{ flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column', gap: 2 }}>
        {/* Widget title */}
        <Paper variant="outlined">
          <Box sx={{ p: 2 }}>
            <TextField
              fullWidth
              size="small"
              label="Title"
              placeholder="e.g. Sky TV Spontaneous Awareness"
              value={title}
              onChange={(e) => onTitleChange(e.target.value)}
            />
            {/* #1247 item 1: the same title controls as the chart editor's
                title row (#1174), writing the same ChartTitleDesign shape.
                Values shown are the resolved ones — this widget's own
                defaults when nothing is overridden. */}
            {onTitleDesignChange && (() => {
              const set = (patch: Partial<ChartTitleDesign>) => onTitleDesignChange({ ...(titleDesign || {}), ...patch })
              const overridden = Boolean(titleDesign && Object.keys(titleDesign).length > 0)
              return (
                <Box data-testid="genai-title-format-row" sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 1.5, flexWrap: 'wrap' }}>
                  <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={titleDesign?.align ?? 'left'}
                    onChange={(_e, v) => { if (v) set({ align: v }) }}
                  >
                    <ToggleButton value="left" aria-label="Align title left"><FormatAlignLeftIcon fontSize="small" /></ToggleButton>
                    <ToggleButton value="center" aria-label="Align title center"><FormatAlignCenterIcon fontSize="small" /></ToggleButton>
                    <ToggleButton value="right" aria-label="Align title right"><FormatAlignRightIcon fontSize="small" /></ToggleButton>
                  </ToggleButtonGroup>
                  <TextField
                    size="small"
                    type="number"
                    value={titleDesign?.font_size ?? 15}
                    onChange={(e) => set({ font_size: Number(e.target.value) })}
                    sx={{ width: 96 }}
                    InputProps={{ endAdornment: <InputAdornment position="end">px</InputAdornment> }}
                    inputProps={{ min: 8, max: 48, 'aria-label': 'Title font size' }}
                  />
                  <FormControl size="small" sx={{ minWidth: 110 }}>
                    <Select
                      value={titleDesign?.font_weight ?? 700}
                      onChange={(e) => set({ font_weight: Number(e.target.value) })}
                      inputProps={{ 'aria-label': 'Title font weight' }}
                    >
                      <MenuItem value={400}>Regular</MenuItem>
                      <MenuItem value={500}>Medium</MenuItem>
                      <MenuItem value={600}>Semibold</MenuItem>
                      <MenuItem value={700}>Bold</MenuItem>
                      <MenuItem value={800}>Extra bold</MenuItem>
                    </Select>
                  </FormControl>
                  <Tooltip title="Title colour">
                    <input
                      type="color"
                      aria-label="Title colour"
                      value={titleDesign?.color ?? '#1A1A1A'}
                      onChange={(e) => set({ color: e.target.value })}
                      style={{ width: 24, height: 24, border: 'none', cursor: 'pointer', padding: 0, background: 'none' }}
                    />
                  </Tooltip>
                  <Tooltip title="Revert title style">
                    <span>
                      <IconButton size="small" aria-label="Revert title style" disabled={!overridden} onClick={() => onTitleDesignChange(undefined)}>
                        <RestartAltIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
              )
            })()}
          </Box>
        </Paper>

        {/* Selected tables (#1132): name + bin + eye, no count label, no
            inline path — the eye's tooltip carries the full path (and job).
            Sits above System Context, where the analysis actually uses it. */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {tables.length === 0 ? (
            <Typography variant="caption" color="text.secondary">
              Click tables in the tree to add them.
            </Typography>
          ) : (
            tables.map((table, index) => (
              // #1247 item 4: each selected table is its own shaded bar — one
              // colour for all, a gap between them, not one block or a hairline.
              <Box
                key={`${table.job || ''}/${table.folderPath || ''}/${table.filename}`}
                data-testid="selected-table-row"
                sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.5, bgcolor: 'grey.100', borderRadius: 1, '&:hover': { bgcolor: 'grey.200' } }}
              >
                <Typography variant="body2" noWrap sx={{ flex: 1, minWidth: 0 }}>
                  {table.displayName}
                </Typography>
                <Tooltip
                  title={`${table.folderPath ? `${table.folderPath}/` : ''}${table.filename}${table.job ? ` (${table.job})` : ''}`}
                >
                  <IconButton size="small">
                    <VisibilityIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <IconButton size="small" onClick={() => handleRemoveTable(index)} color="error" aria-label={`Remove ${table.displayName}`}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            ))
          )}
        </Box>

        {/* Shared: System prompt + Style rules */}
        {systemPrompts && (
          <Accordion disableGutters sx={{ '&:before': { display: 'none' }, border: '1px solid', borderColor: 'divider' }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 36, '& .MuiAccordionSummary-content': { my: 0.5 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="caption" fontWeight={600}>System Context</Typography>
                <Chip label="read-only" size="small" variant="outlined" sx={{ fontSize: '0.65rem', height: 18 }} />
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0, pb: 1 }}>
              <Box sx={{ bgcolor: 'grey.50', borderRadius: 1, p: 1, maxHeight: 300, overflow: 'auto' }}>
                <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word', color: 'text.secondary' }}>
                  {systemPrompts.assembled_system_prompt}
                </Typography>
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, fontStyle: 'italic' }}>
                Base context shared across both table analysis and synthesis.
              </Typography>
            </AccordionDetails>
          </Accordion>
        )}

        {/* Step-based prompt configuration */}
        <StepTabs
          tables={tables}
          analysisInstructions={analysisInstructions}
          synthesisInstructions={synthesisInstructions}
          userContext={userContext}
          synthesisUserContext={synthesisUserContext}
          tableAnalysisPrompts={tableAnalysisPrompts}
          synthesisPrompts={synthesisPrompts}
          onAnalysisInstructionsChange={onAnalysisInstructionsChange}
          onSynthesisInstructionsChange={onSynthesisInstructionsChange}
          onUserContextChange={onUserContextChange}
          onSynthesisUserContextChange={onSynthesisUserContextChange}
          onTableAnalysisPromptSelect={handleTableAnalysisPromptSelect}
          onSynthesisPromptSelect={handleSynthesisPromptSelect}
          analysisGuidedToggles={analysisGuidedToggles}
          synthesisGuidedToggles={synthesisGuidedToggles}
          onAnalysisGuidedTogglesChange={onAnalysisGuidedTogglesChange}
          onSynthesisGuidedTogglesChange={onSynthesisGuidedTogglesChange}
        />
      </Box>
    </Box>
  )
}

export { chipDisplayLabel, extractTableChips, formatAttributionDate, removeTableCitation }

interface MultiTableAnalysisWidgetRendererProps {
  data: MultiTableAnalysisWidgetData
  customer: string
  job: string
  dashboardId?: string
  onViewDetails?: () => void
  onUpdateData?: (data: MultiTableAnalysisWidgetData) => void | Promise<void>
  /** #1256: text edit and sign-off for ANY logged-in user, through the
   *  narrow card endpoint. When present it carries the pencil and the tick
   *  instead of onUpdateData (the whole-config PUT, owner-gated). The
   *  server records the edit history, so the client does not. */
  onCardWrite?: CardWriteHandler
  onOpenSettings?: () => void
  readOnly?: boolean
  /**
   * #1219 gate 2: may this viewer spend tokens regenerating this card —
   * `canOverwriteDashboard(dashboard, userEmail, canPromoteDashboard(...))`,
   * computed once by the page and threaded down through the widget tree.
   * Absent/undefined means false (the server enforces the same rule
   * independently, so this is a UI convenience, not the boundary).
   */
  canRegenerate?: boolean
  globalFilter?: string
  /** Per-job resolved filters for a multi-job dashboard (#1162): job id →
   *  compound filter. When present, the widget uses its OWN job's entry. */
  jobFilters?: Record<string, string>
}

/**
 * The text the reader actually sees: the model's trailing citation-list section
 * removed, and — where the caller asks for it — the inline citations too.
 * Exported so the marker numbering and the reference list under a card are
 * built from exactly the same string the markdown renders (#1296).
 */
export function cleanCitationContent(content: string, stripCitations?: boolean): string {
  // Strip citation list sections from the bottom of the report
  const withoutTrailingList = content.replace(/\n#+\s*(Tables?\s*(Referenced|Cited|Used)|Sources|References|Citations)[\s\S]*$/i, '')
  // Optionally strip inline [Table: ...] citations (used in individual table analysis detail where all citations point to the same table)
  return stripCitations
    ? withoutTrailingList.replace(/\s*\[Table:\s*[^\]]+\]/g, '')
    : withoutTrailingList
}

/**
 * Render markdown content with clickable [Table: ...] citations.
 * Each citation renders as a numbered marker — `[1]` — in info blue, with the
 * full table name (and cited cell, when there is one) on hover (#1296).
 * `numbering` is passed in when the caller also renders a reference list, so
 * the markers and the list carry the same numbers.
 */
const CitationMarkdown: React.FC<{
  content: string
  onTableClick: (tableName: string, cell?: CitationCell) => void
  stripCitations?: boolean
  numbering?: CitationNumbering
}> = ({ content, onTableClick, stripCitations, numbering }) => {
  const cleanedContent = cleanCitationContent(content, stripCitations)
  const citations = numbering ?? createCitationNumbering(cleanedContent)

  return (
    <ReactMarkdown
      components={{
        h1: ({ children }) => (
          <Typography variant="subtitle2" sx={{ mt: 1.5, mb: 0.5, fontWeight: 700, lineHeight: 1.4 }}>
            {children}
          </Typography>
        ),
        h2: ({ children }) => (
          <Typography variant="subtitle2" sx={{ mt: 1.5, mb: 0.5, fontWeight: 700, lineHeight: 1.4 }}>
            {children}
          </Typography>
        ),
        h3: ({ children }) => (
          <Typography variant="subtitle2" sx={{ mt: 1.5, mb: 0.5, fontWeight: 700, lineHeight: 1.4 }}>
            {children}
          </Typography>
        ),
        h4: ({ children }) => (
          <Typography variant="subtitle2" sx={{ mt: 1.5, mb: 0.5, fontWeight: 700, lineHeight: 1.4 }}>
            {children}
          </Typography>
        ),
        p: ({ children }) => (
          <Typography variant="body2" component="div" sx={{ my: 1 }}>
            {processCitations(children, onTableClick, citations)}
          </Typography>
        ),
        li: ({ children }) => (
          <li>
            <Typography variant="body2" component="span">
              {processCitations(children, onTableClick, citations)}
            </Typography>
          </li>
        ),
      }}
    >
      {cleanedContent}
    </ReactMarkdown>
  )
}

/** Process React children, replacing [Table: ...] citations with numbered markers. */
function processCitations(
  children: React.ReactNode,
  onTableClick: (tableName: string, cell?: CitationCell) => void,
  numbering: CitationNumbering,
): React.ReactNode {
  return React.Children.map(children, (child, childIndex) => {
    if (typeof child !== 'string') return child
    return renderCitationText(
      child,
      numbering,
      (reference) => onTableClick(reference.label, reference.cell),
      `citation-${childIndex}`,
    )
  })
}

const REPORT_MARKDOWN_SX = {
  '& h1, & h2, & h3, & h4': {
    mt: 1.5, mb: 0.5,
    fontSize: '0.875rem',
    fontWeight: 600,
    lineHeight: 1.4,
  },
  '& h1:first-of-type, & h2:first-of-type, & h3:first-of-type': { mt: 0 },
  '& p': { fontSize: '0.85rem', lineHeight: 1.5 },
  '& ul, & ol': { my: 0.5, pl: 2 },
  '& li': { my: 0.25, fontSize: '0.85rem' },
} as const

// Shared report card: title + markdown body + edited/signed-off attribution
// footer. Used by both the read-only shared-report branch and the main
// authenticated render path — previously duplicated verbatim in both (#1066).
// `needsReview` only ever applies to the authenticated path (the shared
// branch never computed it), so it defaults to false there, preserving the
// prior shared-branch rendering exactly.
/**
 * The Gen AI card's title (#1247 item 1): the same ChartTitleDesign overrides
 * chart widgets honour (#1044), on top of this widget's own defaults (bold,
 * 0.95rem) so an untouched widget renders exactly as before. One component
 * for every place the title shows — report card, missing-references banner,
 * edit-mode empty state — so they cannot drift.
 */
export const GenAITitle: React.FC<{ title: string; design?: ChartTitleDesign }> = ({ title, design }) => (
  <Typography
    variant="subtitle1"
    sx={{
      mb: 1,
      fontWeight: design?.font_weight ?? 700,
      fontSize: design?.font_size != null ? `${design.font_size}px` : '0.95rem',
      color: design?.color ?? 'text.primary',
      textAlign: design?.align ?? 'left',
    }}
  >
    {title}
  </Typography>
)

/** "AI generated <date, time>" (#1247 item 2). Accepts seconds or millis. */
export function formatGeneratedAt(ts: number): string {
  return new Date(ts < 1e12 ? ts * 1000 : ts)
    .toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
}

interface AnalysisReportCardProps {
  title?: string
  content: string
  onTableClick: (tableName: string, cell?: CitationCell) => void
  editedBy?: MultiTableAnalysisWidgetData['edited_by']
  signedOff?: MultiTableAnalysisWidgetData['signed_off']
  needsReview?: boolean
  titleDesign?: ChartTitleDesign
  /** Unix time the AI text was generated. Shown as "AI generated <date>"
   *  (#1247 item 2, ISO: AI-produced content must be identifiable) — ONLY
   *  while no human has edited the text; an edit supersedes it, since we
   *  cannot tell how much of the text is still the model's. */
  generatedAt?: number
  /** "Based on the … analysis profile, version N." (#2014): the profiles the
   *  AI text read. Shown beside "AI generated", under the same rule. */
  basedOn?: string
  /** The filter a cited table ran under, for the References list (#1296).
   *  Resolved by the widget, which knows which job each table belongs to. */
  citationFilterFor?: (reference: CitationReference) => { filter: string; filterDropped: boolean }
}

const AnalysisReportCard: React.FC<AnalysisReportCardProps> = ({
  title,
  content,
  onTableClick,
  editedBy,
  signedOff,
  needsReview = false,
  titleDesign,
  generatedAt,
  basedOn,
  citationFilterFor,
}) => {
  // One numbering for the whole card, so the markers in the text and the
  // References list underneath agree (#1296).
  const numbering = useMemo(() => createCitationNumbering(cleanCitationContent(content)), [content])
  return (
    <Card variant="outlined">
      <CardContent sx={{ '&:last-child': { pb: 2 } }}>
        {title && <GenAITitle title={title} design={titleDesign} />}
        <Box sx={REPORT_MARKDOWN_SX}>
          <CitationMarkdown content={content} onTableClick={onTableClick} numbering={numbering} />
        </Box>

        <CitationReferenceList
          references={numbering.references}
          filterFor={citationFilterFor}
          onSelect={(reference) => onTableClick(reference.label, reference.cell)}
        />

        {(editedBy || signedOff || generatedAt || basedOn) && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1.5, pt: 1, borderTop: 1, borderColor: 'divider' }}>
            {!editedBy && generatedAt ? (
              <Typography variant="caption" color="text.secondary">
                AI generated {formatGeneratedAt(generatedAt)}
              </Typography>
            ) : null}
            {!editedBy && basedOn ? (
              <Typography variant="caption" color="text.secondary">
                {basedOn}
              </Typography>
            ) : null}
            {editedBy && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <EditIcon sx={{ fontSize: 14, color: 'info.main' }} />
                <Typography variant="caption" color="info.main">
                  Edited by {editedBy.user_email} · {formatAttributionDate(editedBy.edited_at)}
                </Typography>
              </Box>
            )}
            {signedOff && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {needsReview ? (
                  <ErrorOutlineIcon sx={{ fontSize: 14, color: 'warning.main' }} />
                ) : (
                  <CheckCircleIcon sx={{ fontSize: 14, color: 'success.main' }} />
                )}
                <Typography variant="caption" color={needsReview ? 'warning.main' : 'success.main'}>
                  Signed off by {signedOff.user_email} · {formatAttributionDate(signedOff.signed_at)}
                  {needsReview ? ' · lapsed, needs re-review' : ''}
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </CardContent>
    </Card>
  )
}

/**
 * Renderer component for displaying multi-table analysis results.
 * Shows combined report text with clickable table citations and link to view detailed analysis.
 */
export const MultiTableAnalysisWidgetRenderer: React.FC<MultiTableAnalysisWidgetRendererProps> = ({
  data,
  customer,
  job,
  dashboardId,
  onViewDetails,
  onUpdateData,
  onCardWrite,
  onOpenSettings,
  readOnly,
  canRegenerate = false,
  globalFilter,
  jobFilters,
}) => {
  // #1810: the card runs each table under that table's own job's filter.
  // The widget's job (data.job || job) is the job a table without its own
  // belongs to, and on a single-job dashboard the only job the dashboard
  // filter applies to.
  const widgetJob = data.job || job
  const targetPersona = data.persona || 'Executive Summarizer'
  // Whether this surface can persist anything at all. Share-link viewers render
  // through DashboardLayoutComponent without an onCellUpdate, so no write reaches
  // the server — text edit and sign-off must not be offered there.
  // #1256: the narrow card write opens text edit and sign-off to any
  // logged-in user; the whole-config path stays for the editor.
  const canWrite = Boolean(onCardWrite) || Boolean(onUpdateData)
  const cardWrite = onCardWrite && data.multi_table_id
    ? (patch: CardTextWrite) => onCardWrite(data.multi_table_id, patch)
    : undefined

  const buildAnalysisRequest = (force?: boolean, editFoldIn?: string) => ({
    persona: targetPersona,
    user_context: data.user_context,
    synthesis_user_context: data.synthesis_user_context,
    output_length: data.output_length,
    analysis_instructions: data.analysis_instructions,
    synthesis_instructions: data.synthesis_instructions,
    ...(force ? { force: true } : {}),
    // #1144: the user chose "Include my edit" — the edited text rides into the
    // run's synthesis prompt server-side, transient to this run only.
    ...(editFoldIn ? { edit_fold_in_context: editFoldIn } : {}),
    // #1810: the page's filter map; the server keeps only the jobs the
    // card's tables use and snapshots it on the analysis.
    job_filters: cardSendJobFilters(widgetJob, jobFilters, globalFilter),
  })

  const [tableModalOpen, setTableModalOpen] = useState(false)
  const [modalTableName, setModalTableName] = useState('')
  const [modalTableJob, setModalTableJob] = useState('')
  // #1296: the cell a numbered citation pointed at, highlighted in the popup.
  const [modalHighlightCell, setModalHighlightCell] = useState<CitationCell | undefined>(undefined)
  // #1251: the citation popup runs under the filter the cited table's job
  // is under — same resolution as a chart widget (TableGraphWidget's
  // appliedFilter): its own entry on a multi-job dashboard; otherwise the
  // dashboard filter, dropped when the table is from another job.
  const resolveCitationFilter = (tableJob: string) => {
    const filterDropped = jobFilters === undefined && Boolean(tableJob) && tableJob !== job && Boolean(globalFilter)
    return {
      filterDropped,
      filter: jobFilters !== undefined
        ? (jobFilters[tableJob] || '')
        : (filterDropped ? '' : (globalFilter || '')),
    }
  }
  const { filter: modalFilter, filterDropped: modalFilterDropped } = resolveCitationFilter(modalTableJob)
  const [showEditConfirm, setShowEditConfirm] = useState(false)
  const [showSignOffConfirm, setShowSignOffConfirm] = useState(false)
  const {
    isEditing,
    editText,
    setEditText,
    localEditedReport,
    setLocalEditedReport,
    isSaving,
    saveError,
    handleStartEdit: startEditing,
    handleCancelEdit,
    handleSaveEdit: saveEdit,
  } = useCardTextEditor({
    // Through the narrow endpoint when available (#1256): only the text
    // travels, attribution is stamped from the JWT, and the server logs it.
    onUpdateData: cardWrite
      ? (next: MultiTableAnalysisWidgetData) => cardWrite({ edited_report: next.edited_report ?? '' })
      : onUpdateData,
    onRecordEdit: (beforeText, afterText) => {
      if (!dashboardId || cardWrite) return
      axios.post(`${API_BASE_URL}/dashboards/${customer}/${dashboardId}/edit-history`, {
        widget_title: data.title || '',
        widget_section: data.multi_table_id || '',
        edit_type: 'report_edit',
        before_text: beforeText,
        after_text: afterText,
      }).catch(() => { /* best-effort logging */ })
    },
  })
  const [isRegenerating, setIsRegenerating] = useState(false)
  // #1219 gate 2 / #1105: set when a start rejects with the permission-refusal
  // code. Rendered as a dedicated warning (no Retry) ahead of the generic
  // failed-analysis alert, mirroring WizardStep3Preview's `refused` state.
  const [regenerateRefusal, setRegenerateRefusal] = useState<string | null>(null)
  const [showRegenSettings, setShowRegenSettings] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [showRegenConfirm, setShowRegenConfirm] = useState(false)
  // #1144: whether the pending regeneration should fold the manual edit into
  // the prompt ("Include my edit") or run from the data alone ("Discard my
  // edit"). Set at confirm time, read when the run actually starts.
  const [regenFoldInEdit, setRegenFoldInEdit] = useState(false)
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null)
  const [analysisModalItem, setAnalysisModalItem] = useState<MultiTableAnalysisItem | null>(null)
  const [analysesModalOpen, setAnalysesModalOpen] = useState(false)
  const [analysesModalSelectedPath, setAnalysesModalSelectedPath] = useState<string | null>(null)
  const [copyFeedback, setCopyFeedback] = useState(false)
  const [pasteFeedback, setPasteFeedback] = useState(false)

  const CONFIG_CLIPBOARD_KEY = 'mta-widget-config'
  const [clipboardVersion, setClipboardVersion] = useState(0)
  React.useEffect(() => {
    const handler = () => setClipboardVersion(v => v + 1)
    window.addEventListener('mta-config-copied', handler)
    return () => window.removeEventListener('mta-config-copied', handler)
  }, [])
  const hasClipboardConfig = (() => { void clipboardVersion; return Boolean(localStorage.getItem(CONFIG_CLIPBOARD_KEY)) })()

  const handleCopyConfig = () => {
    const config = {
      analysis_instructions: data.analysis_instructions,
      synthesis_instructions: data.synthesis_instructions,
      user_context: data.user_context,
      synthesis_user_context: data.synthesis_user_context,
    }
    localStorage.setItem(CONFIG_CLIPBOARD_KEY, JSON.stringify(config))
    window.dispatchEvent(new Event('mta-config-copied'))
    setCopyFeedback(true)
    setTimeout(() => setCopyFeedback(false), 1500)
  }

  const {
    analyses,
    analysesLoading,
    findAnalysisByPersona,
    isRunning,
    isFailed,
    progress,
    items,
    selectedAnalysisId,
    setSelectedAnalysisId,
    startAnalysis,
    startAnalysisLoading,
    retryItem,
    deleteAnalysis,
  } = useMultiTableAnalysis({
    customer,
    job,
    multiTableId: data.multi_table_id,
  })

  // Find completed analysis for this persona
  const completedAnalysis = findAnalysisByPersona(targetPersona)

  // Missing table reference handling (#970 Part C). Only meaningful when
  // there's a real dashboard to look up the multi-table's current config
  // against — a share-link viewer has no route for it, so the banner simply
  // never shows there, same as every other write-gated affordance on this card.
  const dashboardMultiTable = useDashboardMultiTable({
    customer,
    dashboardId: dashboardId || '',
    multiTableId: data.multi_table_id,
  })
  const configuredTables = dashboardMultiTable.multiTable?.config?.tables || []
  // #1810: the filters this card's tables are under now, one entry per
  // table job — compared with the analysis's snapshot for the banner.
  const currentJobFilters = cardJobFilters(configuredTables, widgetJob, jobFilters, globalFilter)
  // #1229 C6: the same job / page filter / quick filter line the chart
  // widgets carry, one per job the card's tables draw from (#1810).
  const tableJobs = configuredTables.map(t => t.job || widgetJob)
  const signposts = useFilterSignposts(tableJobs.length > 0 ? tableJobs : [widgetJob])
  const { missing: missingTableRefs } = useMissingTableReferences({
    customer,
    job,
    tables: configuredTables,
    enabled: Boolean(completedAnalysis?.combined_report && dashboardId),
  })

  // #1239: every broken reference is decided on the banner, then committed
  // here in ONE pass — one config write, one regeneration — instead of a
  // write + a full re-run per reference. When nothing is left to read, the
  // config is saved and no analysis starts: the card shows its empty state.
  const [applyingReferenceDecisions, setApplyingReferenceDecisions] = useState(false)
  const handleApplyReferenceDecisions = async (decisions: ReferenceDecision[]) => {
    if (!data.multi_table_id) return
    const byPath = new Map(decisions.map(d => [d.ref.fullPath, d]))
    const newTables = configuredTables.flatMap(t => {
      const d = byPath.get(tableFullPath(t))
      if (!d) return [t]
      return d.action === 'replace' ? [d.replacement] : []
    })
    setApplyingReferenceDecisions(true)
    setRegenerateRefusal(null)
    try {
      await dashboardMultiTable.updateMultiTable({ id: data.multi_table_id, config: { tables: newTables } })
      if (newTables.length > 0) await startAnalysis(buildAnalysisRequest(true))
    } catch (err) {
      if (isRegeneratePermissionError(err)) setRegenerateRefusal(extractErrorMessage(err))
    } finally {
      setApplyingReferenceDecisions(false)
    }
  }

  const handlePasteConfig = async () => {
    if (!onUpdateData) return
    const raw = localStorage.getItem(CONFIG_CLIPBOARD_KEY)
    if (!raw) return
    let config: Record<string, unknown>
    try {
      config = JSON.parse(raw)
    } catch { return }
    // Delete existing analysis so widget resets to empty state
    if (completedAnalysis) {
      try { await deleteAnalysis(completedAnalysis.id) } catch { /* continue anyway */ }
    }
    onUpdateData({
      ...data,
      analysis_instructions: (config.analysis_instructions as string) ?? data.analysis_instructions,
      synthesis_instructions: (config.synthesis_instructions as string) ?? data.synthesis_instructions,
      user_context: (config.user_context as string) ?? data.user_context,
      synthesis_user_context: (config.synthesis_user_context as string) ?? data.synthesis_user_context,
      edited_report: undefined,
    })
    setLocalEditedReport(undefined)
    setPasteFeedback(true)
    setTimeout(() => setPasteFeedback(false), 2000)
  }

  // Find any running/pending analysis to show progress from
  const activeAnalysis = analyses.find(a => a.state === 'running' || a.state === 'pending')
  const hasActiveAnalysis = Boolean(activeAnalysis)

  // Clear regenerating flag once the new analysis appears in the list (cache is updated
  // immediately in the mutation's onSuccess, so this fires on the next render)
  if (isRegenerating && (hasActiveAnalysis || isRunning)) {
    setIsRegenerating(false)
  }

  // A fresh start superseded the last refusal — clear it so a retried,
  // successful attempt doesn't leave the stale warning on screen.
  if (regenerateRefusal && (hasActiveAnalysis || isRunning)) {
    setRegenerateRefusal(null)
  }

  // Auto-select active analysis so items query fetches per-table progress
  if (activeAnalysis && selectedAnalysisId !== activeAnalysis.id) {
    setSelectedAnalysisId(activeAnalysis.id)
  }

  // Build a lookup from display/table names to full table paths using the chat context
  const tablePathLookup = useMemo(() => {
    const lookup: Record<string, { path: string; job: string }> = {}
    // Shared viewers have no chat context — the payload embeds a label -> path
    // map next to shared_report instead (#970 Obs 2 follow-up).
    for (const [label, path] of Object.entries(data.shared_table_paths || {})) {
      lookup[label] = { path, job }
    }
    const tableAnalyses = completedAnalysis?.chat_context?.table_analyses || []
    for (const ta of tableAnalyses) {
      // Map by display_name, table_name (filename), and path-derived names
      // #1810: the table's own job, recorded by the synthesis worker; older
      // analyses carry none and fall back to the card's job.
      const entry = { path: ta.table_path, job: ta.job || job }
      if (ta.display_name) lookup[ta.display_name] = entry
      if (ta.table_name) {
        lookup[ta.table_name] = entry
        // Also map without .cbt extension
        lookup[ta.table_name.replace(/\.cbt$/i, '')] = entry
      }
      // Map by the last segment of the path (e.g., "Tables/Foo.cbt" -> "Foo")
      const pathParts = ta.table_path.split('/')
      const lastSegment = pathParts[pathParts.length - 1]
      if (lastSegment) {
        lookup[lastSegment] = entry
        lookup[lastSegment.replace(/\.cbt$/i, '')] = entry
      }
    }
    return lookup
  }, [completedAnalysis, job, data.shared_table_paths])

  /** The job a cited table belongs to — the citation lookup, else this widget's job. */
  const citationJobFor = (tableName: string): string => (
    (tablePathLookup[tableName] || tablePathLookup[tableName.replace(/\.cbt$/i, '')])?.job || job
  )

  const handleTableCitationClick = (tableName: string, cell?: CitationCell) => {
    // Resolve citation name to full table path via the chat context lookup
    const resolved = tablePathLookup[tableName]
      || tablePathLookup[tableName.replace(/\.cbt$/i, '')]
    const tablePath = resolved?.path || tableName
    const tableJob = resolved?.job || job

    setModalTableName(tablePath)
    setModalTableJob(tableJob)
    setModalHighlightCell(cell)
    setTableModalOpen(true)
  }

  /** The filter line for one entry in a card's References list (#1296). */
  const citationFilterFor = (reference: CitationReference) =>
    resolveCitationFilter(citationJobFor(reference.label))

  const handleModalClose = () => {
    setTableModalOpen(false)
    setModalTableName('')
    setModalTableJob('')
    setModalHighlightCell(undefined)
  }

  if (analysesLoading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', p: 1.5 }}>
        <CircularProgress size={24} sx={{ mr: 2 }} />
        <Typography color="text.secondary">Loading analysis...</Typography>
      </Box>
    )
  }

  if (isRunning || hasActiveAnalysis || isRegenerating) {
    const activeProgress = activeAnalysis?.progress || progress
    const progressPercent = activeProgress?.percent_done || 0
    const totalTables = activeProgress?.total || 0
    const completedTables = activeProgress?.completed || 0
    const isSynthesizing = items.length > 0 && items.every(item => item.state === 'completed') && progressPercent < 100

    return (
      <Box sx={{ p: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <CircularProgress size={20} sx={{ mr: 2 }} />
          <Typography variant="body2" fontWeight={500}>
            {isRegenerating ? 'Starting analysis...' :
             isSynthesizing ? 'Synthesizing report...' :
             'Analyzing tables...'}
          </Typography>
        </Box>
        <LinearProgress
          variant={progressPercent > 0 ? 'determinate' : 'indeterminate'}
          value={progressPercent}
          sx={{ mb: 1, height: 6, borderRadius: 3 }}
        />

        {/* Per-table progress list */}
        {items.length > 0 && (
          <List dense disablePadding sx={{ mt: 1 }}>
            {items.map((item) => (
              <ListItem
                key={item.id}
                sx={{
                  py: 0.5,
                  px: 1,
                  cursor: item.state === 'completed' && item.analysis ? 'pointer' : 'default',
                  '&:hover': { bgcolor: 'action.hover' },
                  '&:hover .retry-btn': { opacity: 1 },
                  borderRadius: 1,
                }}
                onClick={() => {
                  if (item.state === 'completed' && item.analysis) {
                    setAnalysisModalItem(item)
                  }
                }}
                secondaryAction={
                  (item.state === 'failed' || item.state === 'running') ? (
                    <IconButton
                      className="retry-btn"
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation()
                        retryItem(item.id)
                      }}
                      sx={{ opacity: 0, transition: 'opacity 0.15s' }}
                      title="Retry this table"
                    >
                      <ReplayIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  ) : undefined
                }
              >
                <Box sx={{ mr: 1.5, display: 'flex', alignItems: 'center' }}>
                  {item.state === 'completed' && <CheckCircleIcon sx={{ color: 'success.main', fontSize: 18 }} />}
                  {item.state === 'running' && <CircularProgress size={16} />}
                  {item.state === 'pending' && <HourglassEmptyIcon sx={{ color: 'text.disabled', fontSize: 18 }} />}
                  {item.state === 'failed' && <ErrorOutlineIcon sx={{ color: 'error.main', fontSize: 18 }} />}
                </Box>
                <ListItemText
                  primary={item.display_name || item.table_name}
                  primaryTypographyProps={{
                    variant: 'body2',
                    color: item.state === 'completed' && item.analysis ? 'info.main' : 'text.primary',
                    fontWeight: item.state === 'completed' ? 500 : 400,
                  }}
                  secondary={item.table_path}
                  secondaryTypographyProps={{ variant: 'caption', noWrap: true }}
                />
              </ListItem>
            ))}
          </List>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
          <Typography variant="caption" color="text.secondary">
            {isSynthesizing ? 'Combining insights into report...' : ''}
          </Typography>
          {totalTables > 0 && (
            <Typography variant="caption" color="text.secondary">
              {completedTables} / {totalTables} tables
            </Typography>
          )}
        </Box>

        {/* Individual table analysis modal (during progress) */}
        <Dialog
          open={Boolean(analysisModalItem)}
          onClose={() => setAnalysisModalItem(null)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6">
                {analysisModalItem?.display_name || analysisModalItem?.table_name || 'Table Analysis'}
              </Typography>
              <IconButton onClick={() => setAnalysisModalItem(null)} size="small">
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent dividers>
            <Box sx={{
              '& h1, & h2, & h3': { mt: 2, mb: 1, fontWeight: 600 },
              '& ul, & ol': { my: 1, pl: 2 },
              '& li': { my: 0.5 },
            }}>
              <CitationMarkdown
                content={analysisModalItem?.analysis || ''}
                onTableClick={handleTableCitationClick}
                stripCitations
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAnalysisModalItem(null)} variant="outlined">
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    )
  }

  // Regenerate-vs-human-edit accept/reject (#963): a force-regenerate ran while a
  // human edit existed, so the fresh AI text is parked here rather than silently
  // overwriting it. Show both versions side by side until the viewer decides.
  if (data.awaiting_review && completedAnalysis?.combined_report) {
    const handleAcceptAI = () => {
      if (!onUpdateData) return
      setLocalEditedReport('')
      onUpdateData({ ...data, edited_report: undefined, edited_by: undefined, awaiting_review: false })
    }
    const handleKeepMine = () => {
      if (!onUpdateData) return
      onUpdateData({ ...data, awaiting_review: false })
    }
    return (
      <Box sx={{ p: 1.5 }}>
        <Alert severity="info" sx={{ mb: 1.5 }}>
          Regenerated. Your edited text was kept — review the fresh AI version below and choose which to keep.
        </Alert>
        <Box sx={{ display: 'flex', gap: 1.5, flexDirection: { xs: 'column', md: 'row' } }}>
          <Paper variant="outlined" sx={{ flex: 1, p: 1.5 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>AI-generated (new)</Typography>
            <Box sx={{ maxHeight: 320, overflow: 'auto', '& p': { fontSize: '0.85rem' } }}>
              <CitationMarkdown content={completedAnalysis.combined_report} onTableClick={handleTableCitationClick} />
            </Box>
            {canWrite && (
              <Button fullWidth size="small" variant="contained" color="info" sx={{ mt: 1.5 }} onClick={handleAcceptAI}>
                Use AI version
              </Button>
            )}
          </Paper>
          <Paper variant="outlined" sx={{ flex: 1, p: 1.5 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>Your edit (kept)</Typography>
            <Box sx={{ maxHeight: 320, overflow: 'auto', '& p': { fontSize: '0.85rem' } }}>
              <CitationMarkdown content={data.edited_report || ''} onTableClick={handleTableCitationClick} />
            </Box>
            {canWrite && (
              <Button fullWidth size="small" variant="outlined" color="info" sx={{ mt: 1.5 }} onClick={handleKeepMine}>
                Keep my edit
              </Button>
            )}
          </Paper>
        </Box>
      </Box>
    )
  }

  // #1219 gate 2 / #1105: a permission refusal is not a failed analysis — no
  // analysis was created, Retry cannot succeed against it, and the server's
  // own wording explains why. Checked ahead of the generic failed-analysis
  // alert below, same priority WizardStep3Preview gives its `refused` state.
  if (regenerateRefusal) {
    return (
      <Box sx={{ m: 2 }}>
        <Alert severity="warning">{regenerateRefusal}</Alert>
      </Box>
    )
  }

  // Check if the most recent analysis for this persona failed
  const failedAnalysis = !completedAnalysis && analyses.some(a => a.persona === targetPersona && a.state === 'failed')
  if (isFailed || failedAnalysis) {
    return (
      <Box sx={{ m: 2 }}>
        <Alert
          severity="error"
          action={
            // #1219 gate 2: this control existed only in the editor before
            // (`!readOnly`) — canRegenerate narrows it further, it does not
            // replace the gate. Keeping `!readOnly` here means an owner
            // viewing (not editing) their own dashboard still sees no Retry,
            // same as today; only the editor path is affected.
            !readOnly && canRegenerate ? (
              <Button
                color="inherit"
                size="small"
                disabled={startAnalysisLoading}
                onClick={() => {
                  void startAnalysis(buildAnalysisRequest()).catch((err) => {
                    if (isRegeneratePermissionError(err)) setRegenerateRefusal(extractErrorMessage(err))
                  })
                }}
              >
                Retry
              </Button>
            ) : undefined
          }
        >
          Analysis failed.
        </Alert>
      </Box>
    )
  }

  // #970 Obs 2: a shared (logged-out) viewer cannot reach the authenticated
  // analysis endpoints, so the generated text arrives embedded in the shared
  // payload as `shared_report`. A human edit (`edited_report`) still wins, the
  // same priority the authenticated path uses. Render it read-only.
  // Gated on `readOnly`: an authenticated editor whose analyses fetch failed
  // (or whose analyses were deleted while `edited_report` persists in widget
  // data) must fall through to the normal editor path below, which shows the
  // "No analysis available yet" placeholder with Run Analysis/Settings —
  // this branch returns unconditionally and would hide those controls.
  const staticReport = data.edited_report || data.shared_report
  if (readOnly && !completedAnalysis?.combined_report && staticReport) {
    return (
      <Box sx={{ p: 1 }}>
        <AnalysisReportCard
          title={data.title}
          titleDesign={data.title_design}
          content={staticReport}
          onTableClick={handleTableCitationClick}
          citationFilterFor={citationFilterFor}
          editedBy={data.edited_by}
          signedOff={data.signed_off}
          generatedAt={data.shared_generated_at}
          basedOn={data.shared_based_on}
        />

        {/* Table citation modal — the shared table-data endpoint serves these
            viewers, so citation chips stay clickable on a share link. */}
        <TableCitationModal
          open={tableModalOpen}
          onClose={handleModalClose}
          tableName={modalTableName}
          customer={customer}
          job={modalTableJob}
          filter={modalFilter}
          filterDropped={modalFilterDropped}
          highlightCell={modalHighlightCell}
        />
      </Box>
    )
  }

  if (!completedAnalysis || !completedAnalysis.combined_report) {
    return (
      <Box sx={{ p: 1.5, textAlign: 'center', color: 'text.secondary' }}>
        {pasteFeedback && (
          <Alert severity="success" sx={{ mb: 1, textAlign: 'left' }}>Context pasted — open Settings to review, then Run Analysis.</Alert>
        )}
        {/* #1247 item 3, EDIT MODE ONLY: the title above the icon and the
            selected tables as removable chips below, so the editor can tell
            which widget this is and what it reads. Preview and share links
            keep the bare icon + line. */}
        {!readOnly && data.title && <GenAITitle title={data.title} design={data.title_design} />}
        <AnalyticsIcon sx={{ fontSize: 40, mb: 1, opacity: 0.5 }} />
        <Typography variant="body2" sx={{ mb: readOnly ? 0 : 1 }}>
          No analysis available yet
        </Typography>
        {!readOnly && (
          <>
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
              {/* #1219 gate 2: hidden, not disabled, when the viewer can't
                  regenerate this card — Settings stays available below so
                  they can still review configuration. */}
              {canRegenerate && (
                <Button
                  variant="contained"
                  color="info"
                  size="small"
                  disabled={startAnalysisLoading || !data.multi_table_id}
                  onClick={() => {
                    void startAnalysis(buildAnalysisRequest()).catch((err) => {
                      if (isRegeneratePermissionError(err)) setRegenerateRefusal(extractErrorMessage(err))
                    })
                  }}
                  startIcon={startAnalysisLoading ? <CircularProgress size={16} /> : undefined}
                >
                  {startAnalysisLoading ? 'Starting...' : 'Run Analysis'}
                </Button>
              )}
              {onOpenSettings && (
                <Button
                  variant="outlined"
                  color="info"
                  size="small"
                  startIcon={<SettingsIcon />}
                  onClick={onOpenSettings}
                >
                  Settings
                </Button>
              )}
            </Box>
            <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center', mt: 1 }}>
              <Tooltip title={copyFeedback ? 'Copied!' : 'Copy Context'}>
                <IconButton size="small" onClick={handleCopyConfig} color={copyFeedback ? 'success' : 'default'}>
                  {copyFeedback ? <CheckCircleIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                </IconButton>
              </Tooltip>
              {onUpdateData && hasClipboardConfig && (
                <Tooltip title="Paste Context">
                  <IconButton size="small" onClick={handlePasteConfig} color="default">
                    <ContentPasteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
            {configuredTables.length > 0 && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, justifyContent: 'center', mt: 1.5 }}>
                {configuredTables.map((t) => {
                  const name = t.displayName || t.filename
                  return (
                    <Chip
                      key={`${t.job || ''}/${t.folderPath || ''}/${t.filename}`}
                      size="small"
                      variant="outlined"
                      label={name}
                    />
                  )
                })}
              </Box>
            )}
          </>
        )}
      </Box>
    )
  }

  // A referenced table has been renamed, deleted, or moved since this card was
  // configured — show the broken-reference recovery UI instead of stale/wrong
  // output (#970 Part C, mirrors #969's table-widget pattern).
  if (missingTableRefs.length > 0) {
    return (
      <Box sx={{ p: 1.5 }}>
        {data.title && <GenAITitle title={data.title} design={data.title_design} />}
        <MissingReferencesBanner
          customer={customer}
          job={job}
          missing={missingTableRefs}
          onApply={(decisions) => { void handleApplyReferenceDecisions(decisions) }}
          applying={applyingReferenceDecisions}
          canApply={canRegenerate}
        />
      </Box>
    )
  }

  // #1239: every reference was removed — there is no data source left, so
  // the stale report must not show and nothing regenerates. Settings is the
  // way back (add tables, then run).
  if (dashboardMultiTable.multiTable && configuredTables.length === 0) {
    return (
      <Box sx={{ p: 1.5, textAlign: 'center', color: 'text.secondary' }}>
        <AnalyticsIcon sx={{ fontSize: 40, mb: 1, opacity: 0.5 }} />
        <Typography variant="body2" sx={{ mb: readOnly || !onOpenSettings ? 0 : 1 }}>
          No tables configured — this card has nothing to analyse.
        </Typography>
        {!readOnly && onOpenSettings && (
          <Button variant="outlined" color="info" size="small" onClick={onOpenSettings}>
            Open Settings
          </Button>
        )}
      </Box>
    )
  }

  // #1810: the filters the analysis ran under (its snapshot) against the
  // ones the card's tables are under now. Only compared once the
  // multi-table's config loaded — without its tables the current map would
  // read as empty (same guard as sourceTablesChangedSinceRegen below).
  const filterChanged = Boolean(dashboardMultiTable.multiTable) &&
    !jobFiltersEqual(completedAnalysis.config?.job_filters, currentJobFilters)
  const hasCurrentFilter = Object.keys(currentJobFilters).length > 0

  // Settings changed since the on-screen output was generated (#970 Part B).
  // Saving config from the flat edit modal never auto-regenerates — the user
  // regenerates explicitly via the ↻ button — so this is derived by comparing
  // the widget's current instructions/context against the snapshot the
  // completed analysis actually ran with, the same way `filterChanged` above
  // compares the global filter. No new persisted field needed.
  //
  // Source tables (#1047) don't live on `completedAnalysis.config` — they're
  // compared against the table paths the analysis actually ran against
  // (`chat_context.table_analyses`), the same source used elsewhere in this
  // component (`tablePathLookup`) to resolve citations. Only compared when
  // `dashboardId` is present: without it (a shared/logged-out viewer)
  // `configuredTables` is always empty, which would otherwise read as every
  // table having been removed.
  const analyzedTablePaths = (completedAnalysis.chat_context?.table_analyses || []).map(ta => ta.table_path)
  // Compare only when the multi-table's config actually loaded. A null
  // multiTable (still loading, or a legacy card referencing a multi-table
  // linked to a different dashboard, where the lookup 404s) makes
  // configuredTables empty — which read as "every table was removed" and put
  // a false "Settings changed" banner on untouched cards.
  const sourceTablesChangedSinceRegen = Boolean(dashboardId) &&
    Boolean(dashboardMultiTable.multiTable) &&
    sourceTablePathsChanged(configuredTables.map(tableFullPath), analyzedTablePaths)
  // #1169: the server resolves {{tables}}/{{month}}/{{date}} into the
  // instructions BEFORE snapshotting them onto the analysis config, while the
  // cell keeps the raw templated text — a plain string comparison is then
  // permanently unequal and pins this banner. Resolve BOTH sides with the
  // analysis's own context (the tables it ran with, the month it ran in)
  // before comparing. Worker-side variables ({{tableName}}, {{latestMonth}},
  // …) are never resolved into the snapshot, so they compare raw-vs-raw.
  const settingsTemplateCtx = {
    tableNames: (completedAnalysis.chat_context?.table_analyses || [])
      .map(ta => ta.display_name)
      .filter(Boolean),
    generatedAt: completedAnalysis.created_at,
  }
  const settingsChangedSinceRegen = Boolean(completedAnalysis.config) && (
    templatedSettingsDiffer(data.analysis_instructions, completedAnalysis.config?.analysis_instructions, settingsTemplateCtx) ||
    templatedSettingsDiffer(data.synthesis_instructions, completedAnalysis.config?.synthesis_instructions, settingsTemplateCtx) ||
    templatedSettingsDiffer(data.user_context, completedAnalysis.config?.user_context, settingsTemplateCtx) ||
    templatedSettingsDiffer(data.synthesis_user_context, completedAnalysis.config?.synthesis_user_context, settingsTemplateCtx) ||
    sourceTablesChangedSinceRegen
  )

  // Use local state as bridge until the prop updates from the API response
  const displayReport = localEditedReport || data.edited_report || completedAnalysis.combined_report || ''

  // Clear local override once the prop catches up
  if (localEditedReport && data.edited_report === localEditedReport) {
    setLocalEditedReport(undefined)
  }

  // A sign-off attests to specific prose. needs_review comes from the server on
  // read; the analysis-id comparison catches the same lapse live, the moment a
  // regenerate completes, without waiting for the dashboard to be refetched (#963).
  const signOffSuperseded = Boolean(
    data.signed_off?.analysis_id &&
    !data.edited_report &&
    completedAnalysis &&
    completedAnalysis.id !== data.signed_off.analysis_id
  )
  const needsReview = Boolean(data.needs_review) || signOffSuperseded

  const handleStartEdit = () => startEditing(displayReport)

  // Confirm dialog before every text edit, every time (#963) — deliberate
  // friction so every viewer knows they're changing shared, attributed content.
  const handleRequestEdit = () => {
    setShowEditConfirm(true)
  }

  const handleConfirmEdit = () => {
    setShowEditConfirm(false)
    handleStartEdit()
  }

  const handleRemoveChip = (label: string) => {
    setEditText(prev => removeTableCitation(prev, label))
  }

  const handleRequestSignOff = () => setShowSignOffConfirm(true)

  const handleConfirmSignOff = () => {
    setShowSignOffConfirm(false)
    if (cardWrite) {
      void cardWrite({ sign_off: true }).catch(() => { /* surfaced by the save snackbar */ })
      return
    }
    if (!onUpdateData) return
    void Promise.resolve(onUpdateData({ ...data, sign_off_requested: true })).catch(() => { /* surfaced by the save snackbar */ })
  }

  // #1256: anyone who can sign can revoke, each act logged. Through the narrow
  // endpoint in view mode; through the one-shot flag on the editor's PUT.
  const handleRevokeSignOff = () => {
    if (cardWrite) {
      void cardWrite({ revoke_sign_off: true }).catch(() => { /* surfaced by the save snackbar */ })
      return
    }
    if (!onUpdateData) return
    void Promise.resolve(onUpdateData({ ...data, sign_off_revoke_requested: true })).catch(() => { /* surfaced by the save snackbar */ })
  }

  // Await the write. A rejected save (a concurrent edit landed first) must leave
  // the editor open with the text intact — not close it and render text the
  // server never accepted (#963).
  const handleSaveEdit = () => { void saveEdit(data, displayReport) }

  const handleRegenerate = () => {
    setShowRegenConfirm(true)
  }

  // #1112: regenerate is reachable from edit mode — Matt's decision: save,
  // then regenerate. The draft is persisted first (becoming `edited_report`),
  // so the normal regenerate path shows the manual-edit choice dialog (#1144)
  // instead of losing the in-progress text. A refused save keeps the editor
  // open with the text intact (#963 semantics), and no regenerate starts.
  const handleRegenerateFromEdit = async () => {
    const saved = await saveEdit(data, displayReport)
    if (saved) handleRegenerate()
  }

  const handleConfirmRegenerate = (foldInEdit: boolean) => {
    setShowRegenConfirm(false)
    setRegenFoldInEdit(foldInEdit)
    if (onUpdateData) {
      // Show settings dialog so user can review/change before running
      setShowRegenSettings(true)
    } else {
      // No update callback — just fire directly
      doRegenerate(foldInEdit)
    }
  }

  const doRegenerate = (foldInEdit: boolean = regenFoldInEdit) => {
    setShowRegenSettings(false)
    setIsRegenerating(true)
    setRegenerateRefusal(null)
    // #1144: a stored manual edit must never silently mask the fresh output —
    // regenerating an edited card was chosen through an explicit
    // include/discard dialog, so whichever way, the mask is cleared and the
    // regenerated text becomes visible. "Include my edit" additionally sends
    // the edited text as transient synthesis context so the new output carries
    // the edit's intent.
    const editedText = localEditedReport || data.edited_report || ''
    setLocalEditedReport(undefined)
    if (onUpdateData) {
      onUpdateData({ ...data, edited_report: undefined, edited_by: undefined, awaiting_review: false })
    }
    // #1219 gate 2 item 3: ANY start rejection — refused or otherwise — must
    // clear isRegenerating, or the card sticks on "Starting analysis..."
    // forever (nothing else ever flips it back, since no analysis was
    // created to make hasActiveAnalysis/isRunning true).
    void startAnalysis(buildAnalysisRequest(true, foldInEdit && editedText ? editedText : undefined)).catch((err) => {
      setIsRegenerating(false)
      if (isRegeneratePermissionError(err)) setRegenerateRefusal(extractErrorMessage(err))
    })
  }

  const handleDeleteAnalysis = async () => {
    setShowDeleteConfirm(false)
    if (completedAnalysis) {
      await deleteAnalysis(completedAnalysis.id)
    }
    setLocalEditedReport(undefined)
    if (onUpdateData) {
      onUpdateData({ ...data, edited_report: undefined })
    }
  }

  return (
    <Box sx={{ p: 1 }}>
      {filterChanged && (
        <Alert
          severity="warning"
          sx={{ mb: 2 }}
          action={
            // #1219 gate 2: `!readOnly` kept — this control existed only in
            // the editor before; canRegenerate narrows it, doesn't widen it
            // onto the view page. See the Retry alert's note above.
            !readOnly && canRegenerate ? (
              <Button
                color="inherit"
                size="small"
                disabled={startAnalysisLoading}
                onClick={handleRegenerate}
              >
                Re-generate
              </Button>
            ) : undefined
          }
        >
          {hasCurrentFilter
            ? 'A filter has been applied or changed since this analysis was generated.'
            : 'The filter used for this analysis has been removed.'}
        </Alert>
      )}

      {/* Settings changed since this output was generated (#970 Part B) — saving
          config never auto-regenerates, so the previous output stays on screen
          until the user explicitly hits ↻. */}
      {settingsChangedSinceRegen && (
        <Alert
          severity="warning"
          sx={{ mb: 2 }}
          action={
            // #1219 gate 2: `!readOnly` kept — see the note above.
            !readOnly && canRegenerate ? (
              <Button color="inherit" size="small" disabled={startAnalysisLoading} onClick={handleRegenerate}>
                Regenerate
              </Button>
            ) : undefined
          }
        >
          Settings changed — data not yet regenerated.
        </Alert>
      )}

      {/* The generation badge (#983, date and time #1136) moved INTO the
          report card as "AI generated <date>" (#1247 item 2) so the shared
          path carries it too and a human edit supersedes it in one place.
          Only the validation warning stays up here. */}
      {completedAnalysis.validation_issues && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
          <Tooltip title={`Some figures could not be matched to the source tables:\n${completedAnalysis.validation_issues}`}>
            <ErrorOutlineIcon sx={{ color: 'warning.main', fontSize: 16 }} />
          </Tooltip>
        </Box>
      )}
      {signposts.length > 0 && (
        <Box sx={{ mb: 0.5, minWidth: 0 }}>
          {signposts.map((line, i) => <FilterSignpostLine key={i} {...line} />)}
        </Box>
      )}

      {/* #1115: the month-based stale-table warning is gone. It compared each
          table's latest month against the consensus month with plain
          inequality, so a table AHEAD of consensus was flagged as stale.
          Unresolvable references remain flagged by MissingReferencesBanner —
          the only reference warning. Month derivation itself stays (it backs
          the latestMonth/previousMonth template variables). */}

      {/* Action buttons. Text-edit and sign-off are open to every viewer regardless
          of role (#963), but only where there is a write path at all — a share-link
          viewer gets no onUpdateData, so the controls are hidden rather than dead.
          Settings stays config-authority-gated behind `readOnly`, unchanged from
          before. Regenerate additionally requires `canRegenerate` (#1219 gate 2) —
          overwrite authority on the dashboard, not just a write path. */}
      {!isEditing && canWrite && (
        <Box sx={{ display: 'flex', gap: 0.5, mb: 1, justifyContent: 'flex-end' }}>
          <Tooltip title="Edit text">
            <IconButton size="small" onClick={handleRequestEdit} color="info" aria-label="Edit text">
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={data.signed_off ? 'Sign off again (this card has changed since the last sign-off)' : 'Sign off this card'}>
            <IconButton size="small" onClick={handleRequestSignOff} color="success" aria-label="Sign off this card">
              <CheckCircleIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {data.signed_off && (
            <Tooltip title={`Revoke sign-off (signed by ${data.signed_off.user_email})`}>
              <IconButton size="small" onClick={handleRevokeSignOff} color="warning" aria-label="Revoke sign-off">
                <UndoIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {!readOnly && onOpenSettings && (
            <Tooltip title="Settings">
              <IconButton size="small" onClick={onOpenSettings} color="info">
                <SettingsIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {/* #1219 gate 2: `!readOnly` kept — the ↻ button existed only in
              the editor before this issue; canRegenerate is an additional
              narrowing there (a non-overwriter loses it too), not a new
              path onto the view page. The view page's existing regenerate
              routes (Save & Regenerate, missing-refs Apply) are unaffected —
              they were already reachable there and stay gated on
              canRegenerate alone. */}
          {!readOnly && canRegenerate && (
            <Tooltip title="Regenerate analysis">
              <span>
                <IconButton size="small" onClick={handleRegenerate} disabled={startAnalysisLoading} color="info">
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          )}
          {!readOnly && (
            <Tooltip title="More actions">
              <IconButton size="small" onClick={e => setMenuAnchor(e.currentTarget)}>
                <MoreVertIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      )}

      {needsReview && !isEditing && (
        <Alert severity="warning" sx={{ mb: 1 }}>
          {signOffSuperseded
            ? 'Needs re-review — this text was regenerated after it was signed off.'
            : 'Needs re-review — data changed since sign-off.'}
        </Alert>
      )}

      {isEditing ? (
        <Box>
          {/* Table-reference chips are delete-only here — remove a citation, but its
              table binding can't be edited/rebound from the text-edit surface (#963). */}
          {extractTableChips(editText).length > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
              {extractTableChips(editText).map(label => (
                <Chip
                  key={label}
                  label={chipDisplayLabel(label)}
                  size="small"
                  onDelete={() => handleRemoveChip(label)}
                  sx={{ bgcolor: '#E3F2FD', color: '#424242', border: '1px solid #BBDEFB' }}
                />
              ))}
            </Box>
          )}
          <TextField
            value={editText}
            onChange={e => setEditText(e.target.value)}
            multiline
            fullWidth
            minRows={12}
            maxRows={30}
            sx={{
              '& .MuiInputBase-root': {
                fontFamily: 'monospace',
                fontSize: '0.85rem',
              },
            }}
          />
          {saveError && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {saveError}
            </Alert>
          )}
          <Box sx={{ display: 'flex', gap: 1, mt: 1, justifyContent: 'flex-end' }}>
            <Button size="small" onClick={handleCancelEdit} disabled={isSaving}>Cancel</Button>
            {/* #1219 gate 2: text edit happens on the view page too (#1256), where
                regenerating means spending tokens on someone else's dashboard —
                there is no "Regenerate AI Insights" header there to have already
                gated this (that control lives only in the editor, and the view
                page's own Run Page/Run All was dead code, now removed). Not
                `canWrite` — canWrite only proves a write path exists, not that
                this viewer may overwrite the dashboard. Not permitted degrades to
                plain Save, never disappears. */}
            {canRegenerate && (
              <Button
                size="small"
                color="info"
                startIcon={<RefreshIcon fontSize="small" />}
                onClick={() => { void handleRegenerateFromEdit() }}
                disabled={isSaving || startAnalysisLoading}
              >
                Save & Regenerate
              </Button>
            )}
            <Button size="small" variant="contained" color="info" onClick={handleSaveEdit} disabled={isSaving}>
              {isSaving ? 'Saving…' : 'Save'}
            </Button>
          </Box>
        </Box>
      ) : (
        <AnalysisReportCard
          title={data.title}
          titleDesign={data.title_design}
          content={displayReport}
          onTableClick={handleTableCitationClick}
          citationFilterFor={citationFilterFor}
          editedBy={data.edited_by}
          signedOff={data.signed_off}
          needsReview={needsReview}
          generatedAt={completedAnalysis.updated_at}
          basedOn={completedAnalysis.chat_context?.based_on}
        />
      )}

      {/* Individual table analyses link */}
      {!isEditing && (completedAnalysis?.chat_context?.table_analyses?.length ?? 0) > 0 && (
        <Box
          sx={{
            mt: 1.5,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            cursor: 'pointer',
            opacity: 0.7,
            transition: 'opacity 0.2s',
            '&:hover': { opacity: 1 },
          }}
          onClick={() => {
            const tableAnalyses = completedAnalysis!.chat_context!.table_analyses!
            setAnalysesModalOpen(true)
            if (tableAnalyses.length > 0) {
              setAnalysesModalSelectedPath(tableAnalyses[0].table_path)
            }
          }}
        >
          <AnalyticsIcon sx={{ fontSize: 16, color: 'info.main' }} />
          <Typography variant="caption" sx={{ color: 'info.main', textDecoration: 'underline' }}>
            View {completedAnalysis!.chat_context!.table_analyses!.length} detailed table analyses
          </Typography>
        </Box>
      )}

      {onViewDetails && !isEditing && (
        <Box sx={{ mt: 2, textAlign: 'right' }}>
          <Button
            size="small"
            endIcon={<OpenInNewIcon />}
            onClick={onViewDetails}
            color="info"
          >
            View Detailed Analysis
          </Button>
        </Box>
      )}

      {/* Individual table analyses split-pane modal */}
      <Dialog
        open={analysesModalOpen}
        onClose={() => { setAnalysesModalOpen(false); setAnalysesModalSelectedPath(null) }}
        maxWidth="xl"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            height: '90vh',
            maxHeight: '900px',
          },
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="h6">Individual Table Analyses</Typography>
              <Typography variant="subtitle2" color="text.secondary">
                {data.title || 'Analysis'} — {completedAnalysis?.chat_context?.table_analyses?.length || 0} tables analyzed
              </Typography>
            </Box>
            <IconButton onClick={() => { setAnalysesModalOpen(false); setAnalysesModalSelectedPath(null) }} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0, display: 'flex', height: '100%' }}>
          {/* Left sidebar with table list */}
          <Box sx={{ width: 300, borderRight: 1, borderColor: 'divider', overflow: 'auto', flexShrink: 0 }}>
            <List sx={{ p: 0 }}>
              {(completedAnalysis?.chat_context?.table_analyses || []).map((ta) => (
                <ListItem key={ta.table_path} disablePadding>
                  <ListItemButton
                    selected={analysesModalSelectedPath === ta.table_path}
                    onClick={() => setAnalysesModalSelectedPath(ta.table_path)}
                    sx={{
                      borderBottom: 1,
                      borderColor: 'divider',
                      '&.Mui-selected': {
                        backgroundColor: 'info.main',
                        color: 'white',
                        '&:hover': { backgroundColor: 'info.dark' },
                        '& .MuiListItemText-primary': { color: 'white' },
                        '& .MuiListItemText-secondary': { color: 'rgba(255,255,255,0.7)' },
                        '& .MuiSvgIcon-root': { color: 'white' },
                      },
                    }}
                  >
                    <ListItemText
                      primary={ta.display_name || ta.table_name}
                      secondary={ta.table_path}
                      primaryTypographyProps={{
                        fontSize: '0.9rem',
                        fontWeight: analysesModalSelectedPath === ta.table_path ? 600 : 400,
                      }}
                      secondaryTypographyProps={{ variant: 'caption', noWrap: true }}
                    />
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation()
                        handleTableCitationClick(ta.table_name)
                      }}
                      sx={{ ml: 1 }}
                    >
                      <OpenInNewIcon fontSize="small" />
                    </IconButton>
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Box>

          {/* Right content area */}
          <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
            {(() => {
              const selectedTA = completedAnalysis?.chat_context?.table_analyses?.find(
                ta => ta.table_path === analysesModalSelectedPath
              )
              if (!selectedTA) {
                return (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                    <Typography color="text.secondary">Select a table from the list to view its analysis</Typography>
                  </Box>
                )
              }
              return (
                <Paper elevation={0} sx={{ p: 3, bgcolor: 'grey.50' }}>
                  <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <Typography variant="h6">{selectedTA.display_name || selectedTA.table_name}</Typography>
                      <IconButton
                        size="small"
                        onClick={() => handleTableCitationClick(selectedTA.table_name)}
                        sx={{ opacity: 0.7, '&:hover': { opacity: 1 } }}
                        title="Open table"
                      >
                        <OpenInNewIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                  <Divider sx={{ mb: 3 }} />
                  <Box sx={{
                    '& h1, & h2, & h3': { mt: 2, mb: 1, fontWeight: 600 },
                    '& ul, & ol': { my: 1, pl: 2 },
                    '& li': { my: 0.5 },
                  }}>
                    <CitationMarkdown
                      content={selectedTA.analysis || ''}
                      onTableClick={handleTableCitationClick}
                      stripCitations
                    />
                  </Box>
                </Paper>
              )
            })()}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setAnalysesModalOpen(false); setAnalysesModalSelectedPath(null) }}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Table citation modal */}
      <TableCitationModal
        open={tableModalOpen}
        onClose={handleModalClose}
        tableName={modalTableName}
        customer={customer}
        job={modalTableJob}
        filter={modalFilter}
        filterDropped={modalFilterDropped}
        highlightCell={modalHighlightCell}
      />

      {/* Regenerate settings dialog */}
      {onUpdateData && (
        <Dialog
          open={showRegenSettings}
          onClose={() => setShowRegenSettings(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Regenerate Analysis</DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                label="Analysis Context"
                placeholder="Extra context for individual table analysis..."
                value={data.user_context || ''}
                onChange={e => onUpdateData({ ...data, user_context: e.target.value || undefined })}
                multiline
                minRows={2}
                maxRows={6}
                fullWidth
                size="small"
              />
              <TextField
                label="Synthesis Context"
                placeholder="Extra context for the synthesis step..."
                value={data.synthesis_user_context || ''}
                onChange={e => onUpdateData({ ...data, synthesis_user_context: e.target.value || undefined })}
                multiline
                minRows={2}
                maxRows={6}
                fullWidth
                size="small"
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowRegenSettings(false)} color="inherit" size="small">
              Cancel
            </Button>
            <Button
              onClick={() => doRegenerate()}
              variant="contained"
              color="info"
              size="small"
              disabled={startAnalysisLoading}
              startIcon={startAnalysisLoading ? <CircularProgress size={16} /> : <RefreshIcon />}
            >
              Regenerate
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* 3-dot menu */}
      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
        <MenuItem onClick={() => { setMenuAnchor(null); handleCopyConfig() }}>
          <ListItemIcon><ContentCopyIcon fontSize="small" /></ListItemIcon>
          <ListItemText>{copyFeedback ? 'Copied!' : 'Copy Context'}</ListItemText>
        </MenuItem>
        {onUpdateData && hasClipboardConfig && (
          <MenuItem onClick={() => { setMenuAnchor(null); handlePasteConfig() }}>
            <ListItemIcon><ContentPasteIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Paste Context</ListItemText>
          </MenuItem>
        )}
        <Divider />
        <MenuItem onClick={() => { setMenuAnchor(null); setShowDeleteConfirm(true) }} sx={{ color: 'error.main' }}>
          <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>

      {/* Regenerate confirmation. A card carrying a manual edit gets an
          explicit warning BEFORE any tokens are spent (#1134), and a choice
          (#1144): fold the edit into the regeneration prompt so the fresh
          output carries it through (default), or discard it and regenerate
          from the data alone. Either way the stored edit stops masking the
          fresh output — it is never silently layered over the new text. */}
      {showRegenConfirm && (() => {
        const hasManualEdit = Boolean(localEditedReport || data.edited_report)
        return (
          <Dialog open={showRegenConfirm} onClose={() => setShowRegenConfirm(false)} maxWidth="xs">
            <DialogTitle>{hasManualEdit ? 'This card has manual edits' : 'Regenerate Analysis'}</DialogTitle>
            <DialogContent>
              {hasManualEdit && (
                <Alert severity="warning" sx={{ mb: 1.5 }}>
                  You have manually edited this card&apos;s text. Regenerating replaces it with
                  fresh AI text — choose what happens to your edit.
                </Alert>
              )}
              {hasManualEdit && (
                <Typography variant="body2" sx={{ mb: 1 }}>
                  &quot;Include my edit&quot; adds your edited text to the regeneration prompt so the
                  new output carries it through where the data still supports it. &quot;Discard my
                  edit&quot; regenerates from the data alone.
                </Typography>
              )}
              <Typography variant="body2">
                This will use AI tokens to regenerate the analysis.
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setShowRegenConfirm(false)} color="inherit" size="small">
                Cancel
              </Button>
              {hasManualEdit ? (
                <>
                  <Button
                    onClick={() => handleConfirmRegenerate(false)}
                    variant="outlined"
                    color="warning"
                    size="small"
                  >
                    Discard my edit
                  </Button>
                  <Button
                    onClick={() => handleConfirmRegenerate(true)}
                    variant="contained"
                    color="info"
                    size="small"
                  >
                    Include my edit
                  </Button>
                </>
              ) : (
                <Button
                  onClick={() => handleConfirmRegenerate(false)}
                  variant="contained"
                  color="info"
                  size="small"
                >
                  Regenerate
                </Button>
              )}
            </DialogActions>
          </Dialog>
        )
      })()}

      {/* Text-edit confirmation dialog — shown every time, by design (#963) */}
      {showEditConfirm && (
        <Dialog open={showEditConfirm} onClose={() => setShowEditConfirm(false)} maxWidth="xs">
          <DialogTitle>Edit Text</DialogTitle>
          <DialogContent>
            <Typography variant="body2">
              Are you sure you want to edit this text? Your change will be saved in place, attributed to you.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowEditConfirm(false)} color="inherit" size="small">
              Cancel
            </Button>
            <Button onClick={handleConfirmEdit} variant="contained" color="info" size="small">
              Edit
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Card sign-off confirmation — states plainly what a sign-off claims and
          what revokes it, so nobody signs without knowing either (#963). */}
      {showSignOffConfirm && (
        <Dialog open={showSignOffConfirm} onClose={() => setShowSignOffConfirm(false)} maxWidth="xs">
          <DialogTitle>Sign off this card</DialogTitle>
          <DialogContent>
            <Typography variant="body2">
              This records that you have read this card's text and approve it, against today's data.
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              It lapses to "needs re-review" if the text is regenerated, if anyone edits it, or when
              new data lands for this job.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowSignOffConfirm(false)} color="inherit" size="small">
              Cancel
            </Button>
            <Button onClick={handleConfirmSignOff} variant="contained" color="success" size="small">
              Sign off
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Delete confirmation dialog */}
      {showDeleteConfirm && (
        <Dialog open={showDeleteConfirm} onClose={() => setShowDeleteConfirm(false)} maxWidth="xs">
          <DialogTitle>Delete Analysis</DialogTitle>
          <DialogContent>
            <Typography variant="body2">
              Are you sure you want to delete this analysis? This cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowDeleteConfirm(false)} color="inherit" size="small">
              Cancel
            </Button>
            <Button onClick={handleDeleteAnalysis} variant="contained" color="error" size="small">
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      )}

    </Box>
  )
}

export default {
  Editor: MultiTableAnalysisWidgetEditor,
  Renderer: MultiTableAnalysisWidgetRenderer,
}
